import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../../../utils/httpRequest";
import { ThoughtLeader } from "../../../../stores/CoursePreviewStore/Types";
import Leader from "./Leader";

interface Props {
  courseSlug: string;
}

interface State {
  loading: boolean;
  thoughtLeaders: ThoughtLeader[];
  // True when the cards run past the edge of the track. Two or three cards fit
  // side by side on a desktop screen, and then there is nothing to scroll to:
  // the arrows go and the cards sit in the middle. A phone always scrolls.
  overflowing: boolean;
}

class ThoughtLeaders extends Component<Props, State> {
  tutorTrackRef: React.RefObject<HTMLDivElement>;
  trackObserver?: ResizeObserver;

  constructor(props: Props) {
    super(props);

    this.tutorTrackRef = React.createRef();
    this.state = {
      thoughtLeaders: [],
      loading: true,
      overflowing: true,
    };
  }

  componentDidMount() {
    const { courseSlug } = this.props;

    if (!courseSlug) return null;

    return getRequest(`/api/courses/${courseSlug}/thought_leaders.json`).then(
      (response: AxiosResponse) => {
        this.setState({ thoughtLeaders: response.data, loading: false });
      },
    );
  }

  // The track only exists once the leaders have arrived. Measuring here, before
  // the browser paints, means the arrows never flash up and disappear.
  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (prevState.thoughtLeaders !== this.state.thoughtLeaders) {
      this.watchTrack();
    }
  }

  componentWillUnmount() {
    this.trackObserver?.disconnect();
    window.removeEventListener("resize", this.measureTrack);
  }

  watchTrack = () => {
    const track = this.tutorTrackRef.current;
    if (!track) return;

    this.measureTrack();

    if (typeof ResizeObserver !== "undefined") {
      this.trackObserver?.disconnect();
      this.trackObserver = new ResizeObserver(() => this.measureTrack());
      this.trackObserver.observe(track);
    } else {
      window.addEventListener("resize", this.measureTrack);
    }
  };

  measureTrack = () => {
    const track = this.tutorTrackRef.current;
    if (!track) return;

    // A pixel of slack for rounding: three cards at a third each can come out
    // a fraction wider than the track.
    const overflowing = track.scrollWidth - track.clientWidth > 1;
    if (overflowing !== this.state.overflowing) {
      this.setState({ overflowing });
    }
  };

  scrollTutors = (direction: number) => {
    const track = this.tutorTrackRef.current;
    if (!track) return;

    const card = track.querySelector(".tutor-card") as HTMLElement;
    const amount = card ? card.offsetWidth + 20 : 320;

    track.scrollBy({
      left: direction * amount,
      behavior: "smooth",
    });
  };

  render() {
    const { loading, thoughtLeaders, overflowing } = this.state;
    const hasTutors = thoughtLeaders.length > 0;
    const sectionMode =
      thoughtLeaders.length === 1
        ? " is-single"
        : thoughtLeaders.length > 1
          ? ` is-multi${overflowing ? "" : " all-in-view"}`
          : "";
    const showArrows = thoughtLeaders.length > 1 && overflowing;

    if (!loading && !hasTutors) return null;

    return (
      <section
        className={`tutor-section${sectionMode}`}
        id="course_thought_leaders"
        aria-labelledby="tutor-title"
      >
        {loading && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}

        {hasTutors && (
          <div className="container">
            <div className="head">
              <h2 className="section-title" id="tutor-title">
                Taught by the world's{" "}
                <span className="nobr">
                  <em className="hl">best practitioners</em>.
                </span>
              </h2>
            </div>

            <div className="tutor-carousel">
              {showArrows && (
                <button
                  type="button"
                  className="tutor-nav prev"
                  aria-label="Previous tutors"
                  onClick={() => this.scrollTutors(-1)}
                >
                  ‹
                </button>
              )}

              <div className="tutor-track" ref={this.tutorTrackRef} role="list">
                {thoughtLeaders.map((leader) => (
                  <Leader leader={leader} key={leader.id} />
                ))}
              </div>

              {showArrows && (
                <button
                  type="button"
                  className="tutor-nav next"
                  aria-label="Next tutors"
                  onClick={() => this.scrollTutors(1)}
                >
                  ›
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    );
  }
}

export default ThoughtLeaders;
